import { t } from "i18next";
import axios from "../helpers/axios";
import {
  IBillingAddressRequest,
  IEditCreatorChatDefaultPageRequest,
  IEditCreatorPersonalInfosRequest,
  IEditPersonalInfosRequest,
} from "../helpers/requests";
import { IUserPaymentFrequencyResponse } from "../helpers/responses";
import {
  IBankingSetting,
  IBillingAddress,
  IDetailedRevenueCategory,
  IHelpContactRequest,
  IMonetization,
  INotification,
  IOtherUserInfoWrapper,
  IRevenue,
  ISponsorship,
  IUser,
  IUserBanks,
  IUserInfos,
  IUserSettings,
  IUserShortInfosResponse,
  IVatCountryResponse,
  PageableData,
} from "../helpers/types";
import { authService } from "./auth.service";
import { socketService } from "./socket.service";

class UserService {
  public getMyProfile = (token?: string) => {
    const url = `/users/me`;
    return new Promise<IUser>((resolve, reject) => {
      const config = token
        ? {
            headers: {
              Authorization: token,
            },
          }
        : {};
      axios
        .get(url, config)
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject("Something went wrong.");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public getUserInfos = (includePrivateInfos: boolean = false) => {
    const url = `/users/infos?ipi=${includePrivateInfos}`;
    return new Promise<IUserInfos>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject("Something went wrong.");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public getUserSettings = () => {
    const url = `/user-settings/infos`;
    return new Promise<IUserSettings>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject("Something went wrong.");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public getOtherUserInfos = (idOrUsername: string) => {
    const url = `/users/view/${idOrUsername}`;
    return new Promise<IOtherUserInfoWrapper>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else if (response?.data?.message) reject(response.data.message);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public getProfile = (id: string) => {
    const url = `/users/${id}/view`;
    return new Promise<IUser>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else if (response?.data?.message) reject(response.data.message);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public getPublicProfile = (username: string) => {
    const url = `/users/public/${username}`;
    return new Promise<IUser>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else if (response?.data?.message) reject(response.data.message);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public updateMyProfile = (data: IUser) => {
    const url = `/users/me`;
    return new Promise<IUser>((resolve, reject) => {
      axios
        .put(url, data)
        .then((response) => {
          if (response?.data?.data) {
            resolve(response.data.data);
          } else {
            reject(response.data.message);
          }
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public updateUserInfos = (data: IEditPersonalInfosRequest | IEditCreatorPersonalInfosRequest) => {
    const url = `/users/infos`;
    return new Promise<IUserInfos>((resolve, reject) => {
      axios
        .patch(url, data)
        .then((response) => {
          if (response?.data?.data) {
            resolve(response.data.data);
          } else {
            reject(response.data.message);
          }
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public updateToDefaultAvatar = () => {
    const url = `/users/editToDefaultAvatar`;
    return new Promise<string>((resolve, reject) => {
      axios
        .put(url)
        .then((response) => {
          if (response?.data?.data) {
            resolve(response.data.data);
          } else {
            reject(response.data.message);
          }
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public changePassword = (currentPassword: string, newPassword: string, confirmPassword: string, deviceId: string) => {
    const url = `/users/me/change-password`;
    return new Promise<void>((resolve, reject) => {
      axios
        .put(
          url,
          { currentPassword, newPassword, confirmPassword, deviceId },
          {
            headers: {
              ...socketService.getSocketIdForHeaders(),
              "refresh-token": authService.getRefreshToken(), //NOTE: TO BE ABLE TO DELETE IT IN THE BACKEND
            },
          }
        )
        .then((response) => {
          if (response?.data) {
            if (response.data?.data?.isRefreshTokenChanged) {
              authService.setRefreshToken(response.data?.data?.newRefreshToken, false);
            }

            resolve(response?.data);
          } else if (response?.data?.message) resolve(response?.data?.message);
          else reject("Something went wrong.");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public updateCoverImage = (formData: FormData) => {
    const url = `/users/me/cover`;
    return new Promise<void>((resolve, reject) => {
      axios
        .put(url, formData)
        .then((response) => {
          if (response?.data?.data?.success && response?.data?.data?.url) resolve();
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public updateAvatarImage = (formData: FormData) => {
    const url = `/users/me/avatar`;
    return new Promise<string>((resolve, reject) => {
      axios
        .put(url, formData)
        .then((response) => {
          if (response?.data?.data) resolve(response?.data?.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public updateCreatorContentTypes(contentTypes: string[]) {
    const url = `/auth/register/creator/content-types`;
    return new Promise<void>((resolve, reject) => {
      axios
        .post(
          url,
          { contentTypes },
          {
            headers: {
              Authorization: authService.getToken("token"),
            },
          }
        )
        .then((response) => {
          if (response?.data?.data) {
            if (response.data.data.token) {
              authService.setToken(response.data.data.token, false);
            }
            resolve();
          } else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  }

  public updateCreatorMonetization(monetization: IMonetization) {
    const url = `/auth/register/creator/monetization`;
    return new Promise<void>((resolve, reject) => {
      axios
        .post(url, monetization, {
          headers: {
            Authorization: authService.getToken("token"),
          },
        })
        .then((response) => {
          if (response?.data?.data) {
            if (response.data.data.token) {
              authService.setToken(response.data.data.token, false);
            }
            resolve();
          } else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  }

  updateBillingAddress(billingAddress: IBillingAddress) {
    const url = `/auth/register/creator/billingAddress`;
    return new Promise<void>((resolve, reject) => {
      axios
        .post(url, { ...billingAddress }, { headers: { Authorization: authService.getToken("token") } })
        .then((response) => {
          if (response?.data?.data) {
            if (response.data.data.token) {
              authService.setToken(response.data.data.token, false);
            }
            resolve();
          } else reject(t("something-want-wrong"));
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject(t("something-want-wrong"));
        });
    });
  }

  updateBillingAddressInfos(payload: IBillingAddressRequest) {
    const url = `/users/creator/billingAddressInfos`;
    return new Promise<IUserInfos>((resolve, reject) => {
      axios
        .patch(url, payload)
        .then((response) => {
          if (response?.data?.data) resolve(response?.data?.data);
          else reject(t("something-want-wrong"));
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject(t("something-want-wrong"));
        });
    });
  }

  public uploadIdFiles = (formData: FormData) => {
    const url = `/auth/register/creator/id-files`;
    return new Promise<void>((resolve, reject) => {
      axios
        .post(url, formData, { headers: { Authorization: authService.getToken("token") } })
        .then((response) => {
          if (response?.data?.data) {
            if (response.data.data.token) {
              authService.setToken(response.data.data.token, false);
            }
            resolve();
          } else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public search = (param: any) => {
    const url = `/users/search/creatorsv2?q=${param.q ? param.q : ""}&limit=${param.limit ? param.limit : ""}&offset=${
      param.offset ? param.offset : ""
    }&sortBy=${param.sortBy ? param.sortBy : ""}&sort=${param.sort ? param.sort : ""}&category=${param.category ? param.category : ""}&getSelf=${
      param.getSelf ? param.getSelf : "true"
    }`;
    return new Promise<PageableData<IUserShortInfosResponse>>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.data) {
            resolve(response.data.data);
          } else reject("Something went wrong.");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public getCreatorMonetizations = (creatorId: any) => {
    const url = `/users/creator/${creatorId}/monetizations`;
    return new Promise<IMonetization>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public creatorSponsorship = () => {
    const url = `/earning/creator/sponsorship`;
    return new Promise<ISponsorship>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public isVerified = (id: string) => {
    const url = `/users/${id}/isVerified`;
    return new Promise<boolean>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.status === 0) resolve(response.data.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public getRevenue = (
    graphData: string,
    limit?: number,
    offset?: number,
    typeFilter?: string,
    reportType?: string,
    includeStats: boolean = true
  ) => {
    let url = "";
    if (offset !== undefined && limit) {
      url = `/earning/twizz/stats?type=${graphData}&limit=${limit}&offset=${offset}&typeFilter=${typeFilter || ""}&includeStats=${includeStats}`;
      if (reportType) {
        url += `&reportType=${reportType}`;
      }
    } else {
      url = `/earning/twizz/stats?type=${graphData}&typeFilter=${typeFilter || ""}&includeStats=${includeStats}`;
      if (reportType) {
        url += `&reportType=${reportType}`;
      }
    }
    return new Promise<IRevenue>((resolve, reject) => {
      axios
        .get(url, {
          headers: {
            Authorization: authService.getToken("token"),
          },
        })
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public getDetailedRevenueCategory = (fromDate: string, toDate: string) => {
    const url = `/earning/twizz/categoryTotalEarning?fromDate=${fromDate}&toDate=${toDate}`;
    return new Promise<IDetailedRevenueCategory>((resolve, reject) => {
      axios
        .get(url, {
          headers: {
            Authorization: authService.getToken("token"),
          },
        })
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public updateMonetizations = (monetizations: IMonetization) => {
    const url = `/users/creator/monetizations`;
    return new Promise<IMonetization>((resolve, reject) => {
      axios
        .put(url, monetizations)
        .then((response) => {
          if (response?.data) resolve(response.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public getCreatorBanks = () => {
    const url = `/users/creator/banks`;
    return new Promise<IUserBanks[]>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public updatePayoutFrequency = (payload: { paymentFrequency: number; payout_status: boolean }) => {
    const url = `/users/creator/paymentFrequency`;
    return new Promise<IUserPaymentFrequencyResponse>((resolve, reject) => {
      axios
        .patch(url, payload)
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public setDefaultBankAccount = (id: string) => {
    const url = `/users/creator/sdba/${id}`;
    return new Promise<{ changed: boolean }>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.data) resolve(response.data?.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public createBankingSetting = (payload: IBankingSetting) => {
    const url = `/users/creator/new-bank`;
    return new Promise<IUserBanks>((resolve, reject) => {
      axios
        .post(url, payload)
        .then((response) => {
          if (response?.data?.data) resolve(response.data?.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public deleteBankAccount = (id: string) => {
    const url = `/users/creator/bankAccount/${id}`;
    return new Promise<{ deleted: boolean }>((resolve, reject) => {
      axios
        .delete(url)
        .then((response) => {
          if (response?.data?.data) resolve(response.data?.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public getRecentSearch = (seeAll: boolean = false) => {
    const url = `/users/search/searchHistory?seeAll=${seeAll}`;
    return new Promise<any>((resolve, reject) => {
      axios
        .get(url, {
          headers: {
            Authorization: authService.getToken("token"),
          },
        })
        .then((response) => {
          if (response?.data) resolve(response.data.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public addRecentSeacrchUser = (id: String) => {
    const url = `/users/search/searchHistory/${id}`;
    return new Promise<void>((resolve, reject) => {
      axios
        .put(url, {
          headers: {
            Authorization: authService.getToken("token"),
          },
        })
        .then(() => {
          resolve();
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public deleteRecetSearchUser = (id: String) => {
    const url = `/users/search/searchHistory/${id}`;
    return new Promise<void>((resolve, reject) => {
      axios
        .delete(url, {
          headers: {
            Authorization: authService.getToken("token"),
          },
        })
        .then(() => {
          resolve();
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public updateDateOfBirth = (
    email: string,
    firstName: string,
    lastName: string,
    username: string,
    date: any,
    isFan: boolean,
    phone: string,
    phoneCode: string,
    gender: string,
    name?: string,
    instagram?: string
  ) => {
    const url = `/users/update/dob`;
    return new Promise<any>((resolve, reject) => {
      axios
        .patch(
          url,
          {
            email,
            name,
            firstName,
            lastName,
            username,
            dateOfBirth: date,
            isFan,
            phone,
            phoneCode,
            gender,
            instagram,
          },
          {
            headers: {
              Authorization: authService.getToken("token"),
            },
          }
        )
        .then((response) => {
          if (response?.data?.data) {
            if (response.data.data.token) {
              authService.setToken(response.data.data.token, false);
            }
            resolve(response?.data?.data);
          } else reject(response?.data?.message);
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public getVatCountries = () => {
    const url = `/countries/vatCountry`;
    return new Promise<IVatCountryResponse>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public getUserIdByUsername = (username: string) => {
    const url = `/users/getId/${username}`;
    return new Promise<any>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else if (response?.data?.message) reject(response.data.message);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public helpContactSupport = (helpContactRequest: IHelpContactRequest) => {
    const url = `/support/create`;
    return new Promise<void>((resolve, reject) => {
      axios
        .post(url, helpContactRequest)
        .then((response) => {
          if (response?.data) resolve(response?.data);
          else if (response?.data?.message) resolve(response?.data?.message);
          else reject("Something went wrong.");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public updatePreferredLanguage = (preferredLang: string) => {
    const url = `/user-settings/update/preferredLang`;
    return new Promise<IUserSettings>((resolve, reject) => {
      axios
        .patch(url, {
          preferredLang,
        })
        .then((response) => {
          if (response?.data?.data) resolve(response?.data?.data);
          else reject(response?.data?.message);
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public updateUserSettingsDefaultPageAndChat = (payload: IEditCreatorChatDefaultPageRequest) => {
    const url = `/user-settings/page-chat-settings`;
    return new Promise<IUserSettings>((resolve, reject) => {
      axios
        .patch(url, {
          ...payload,
        })
        .then((response) => {
          if (response?.data?.data) resolve(response?.data?.data);
          else reject(response?.data?.message);
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public getFollowedCreator = () => {
    const url = `/reactions/creators/followed`;
    return new Promise<any>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.data !== undefined) resolve(response.data.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public addFanPhoneNumber = (phone: string, phoneCode: string, countryCode: string, acceptsPromotionsBySms: boolean) => {
    const url = `/users/fan/phone/number`;
    return new Promise<any>((resolve, reject) => {
      axios
        .put(url, {
          phone,
          phoneCode,
          countryCode,
          acceptsPromotionsBySms,
        })
        .then((response) => {
          if (response?.data?.data.message) resolve(response.data.data);
          else reject(response?.data?.message || "Une erreur s'est produite");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public updatePhoneModalShown = () => {
    const url = `/user-settings/update/phone/modal/shown`;
    return new Promise<IUserSettings>((resolve, reject) => {
      axios
        .put(url, {
          phoneModalShown: true,
        })
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject(response?.data?.message || "Une erreur s'est produite");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public updateUserSettingsStatus = (setting: string) => {
    const url = `/user-settings/update/user-settings`;
    return new Promise<IUserSettings>((resolve, reject) => {
      axios
        .patch(url, {
          setting,
        })
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject(response?.data?.message || "Une erreur s'est produite");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public updateNotificationSetting = (monetizations: INotification) => {
    const url = `/user-settings/update/notification`;
    return new Promise<INotification>((resolve, reject) => {
      axios
        .patch(url, monetizations)
        .then((response) => {
          if (response?.data) resolve(response.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public updateTutorialVisibilityStatus = (fieldName: string) => {
    const url = `/user-settings/update-modal-status`;
    return new Promise<IUserSettings>((resolve, reject) => {
      axios
        .patch(url, { fieldName })
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };

  public getCreatorChatTypeInfos = (creatorId: string) => {
    const url = `/user-settings/settings/chatType/${creatorId}`;
    return new Promise<{ chat: string }>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject("Something went wrong.");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong.");
        });
    });
  };

  public getCreatorsProfiles = () => {
    const url = `/creators-profiles`;
    return new Promise<{ username: string; name: string }[]>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data) {
            resolve(response.data);
          } else {
            reject("Something went wrong.");
          }
        })
        .catch((error) => {
          console.error(`Error fetching creators profiles: ${error?.message}`);
          if (error?.response?.data?.message) {
            reject(error.response.data.message);
          } else {
            reject("Something went wrong.");
          }
        });
    });
  };

  public deleteAccount = () => {
    const url = `/users/delete`;
    return new Promise<{ deleted: boolean }>((resolve, reject) => {
      axios
        .delete(url)
        .then((response) => {
          if (response?.data?.data) resolve(response.data.data);
          else reject("Something went wrong");
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject("Something went wrong");
        });
    });
  };
}

export const userService = new UserService();
